/**
 * Recurring documents — the wire contract for `apps/api/src/routes/accounting/recurring.ts`
 * (`/api/v1/recurring/orgs/:orgId/templates…`) and the web hooks
 * (`useRecurringTemplates.ts`). One template row per schedule; the four
 * document types share the schedule/state machine and differ only in the
 * `payload`, which is EXACTLY the existing create wire body minus its dates.
 *
 * ── PAYLOAD BY REUSE, NOT BY COPY ───────────────────────────────────────────
 * Each payload variant is the corresponding create schema with its date
 * field(s) `.omit()`-ed:
 *   ap_bill    → `apBillCreateFieldsSchema` − {invoice_date, due_date}   (keeps invoice_number)
 *   ar_invoice → `createARInvoiceSchema`    − {invoice_date, due_date}
 *   expense    → `createExpenseSchema`      − {expense_date}
 *   journal    → `createJournalTransactionSchema` − {transaction_date}
 * so a field added to a create body is a field of the template payload the
 * moment it lands, and `.strict()` is inherited (a date key left in the
 * payload is REJECTED — the generator supplies the occurrence date). The SQL
 * core (`recurring_generate_for_template`) reads these keys by name and calls
 * the same four create RPCs the routes call today — the key contract is
 * asserted end-to-end by `recurring/payload.fixtures.json`, parsed here in
 * Vitest and inserted → generated by the manual SQL suite.
 *
 * The AP payee XOR is a `.refine()` on `createAPBillSchema` that `.omit()`
 * drops, so it is re-applied in the template `superRefine` (path `payload`).
 *
 * ── DATES ───────────────────────────────────────────────────────────────────
 * `start_date` / `end_date` are calendar days (`calendarDateSchema`: real
 * dates, `2026-02-31` refused). `due_days` (AP/AR only — the table CHECK
 * `due_days_by_type`) turns the occurrence date into the document's due date
 * at generation; expense/journal bodies REJECT it via `.strict()`.
 *
 * ── SERVER-COMPUTED STATE IS NEVER IN A BODY ────────────────────────────────
 * `status`, `next_occurrence_date`, `occurrences_generated`, `owner_id`,
 * `anchor_day` … are computed by the create/update/status RPCs (Pattern B+)
 * and only ever READ through the wire types below.
 */
import { z } from "zod";
import { RECURRING_FREQUENCIES, maxIntervalCountFor, } from "../recurring/schedule.js";
import { apBillCreateFieldsSchema, apBillPayeeXor } from "./accountsPayable.js";
import { arLineItemSchema, createARInvoiceSchema } from "./accountsReceivable.js";
import { calendarDateSchema } from "./dateWindow.js";
import { createExpenseSchema } from "./expenses.js";
import { createJournalTransactionSchema } from "./ledgerTransactions.js";
// ---------------------------------------------------------------------------
// Vocabulary — the table CHECK lists
// ---------------------------------------------------------------------------
export const RECURRING_DOCUMENT_TYPES = ["ap_bill", "ar_invoice", "expense", "journal"];
export const recurringDocumentTypeSchema = z.enum(RECURRING_DOCUMENT_TYPES);
/** `RecurringFrequency` itself is exported by `recurring/schedule.ts` (the rule's home). */
export const recurringFrequencySchema = z.enum(RECURRING_FREQUENCIES);
export const RECURRING_TEMPLATE_STATUSES = ["active", "paused", "ended", "error"];
/**
 * `recurring_document_occurrences.issue_deferred_code` (20261101120000, CHECK
 * list, widened by 20261101130000): why an `auto_issue` AR occurrence was
 * left a draft — the issue-date rule refused its date, or a 0% / exempt line
 * states no exemption reason (`EXEMPTION_REASON_REQUIRED`). Null on every
 * other row.
 */
export const RECURRING_ISSUE_DEFERRED_CODES = [
    "ISSUE_DATE_FUTURE",
    "ISSUE_DATE_BEFORE_LAST",
    "VAT_PERIOD_FILED",
    "EXEMPTION_REASON_REQUIRED",
];
// ---------------------------------------------------------------------------
// Payload variants — the create bodies minus their dates
// ---------------------------------------------------------------------------
/** AP bill payload: keeps `invoice_number` (the form suggests `BILL-{{YYYY}}{{MM}}`). */
export const recurringApBillPayloadSchema = apBillCreateFieldsSchema.omit({
    invoice_date: true,
    due_date: true,
});
/**
 * AR payload: the create body minus its dates, with `line_items` put BACK to
 * the base AR line (`arLineItemSchema`). The create body's lines carry the
 * optional rebill link `source_expense_id`; a template must never carry one —
 * every generated invoice would re-bill the same expense — so `.strict()`
 * rejects the key here. `supply_date` is a date too and goes with them: each
 * occurrence supplies on its own occurrence date (the generator passes none).
 */
export const recurringArInvoicePayloadSchema = createARInvoiceSchema
    .omit({
    invoice_date: true,
    due_date: true,
    supply_date: true,
})
    .extend({
    line_items: z.array(arLineItemSchema).min(1),
});
export const recurringExpensePayloadSchema = createExpenseSchema.omit({ expense_date: true });
export const recurringJournalPayloadSchema = createJournalTransactionSchema.omit({
    transaction_date: true,
});
/**
 * The payload schema for a given type — what `hydrate*FromPayload` (web) and
 * any server consumer use to re-validate a stored `payload` jsonb before
 * trusting its shape.
 */
export const recurringPayloadSchemaByType = {
    ap_bill: recurringApBillPayloadSchema,
    ar_invoice: recurringArInvoicePayloadSchema,
    expense: recurringExpensePayloadSchema,
    journal: recurringJournalPayloadSchema,
};
// ---------------------------------------------------------------------------
// Schedule fields — shared by all four variants
// ---------------------------------------------------------------------------
/** `name` — CHECK (char_length(btrim(name)) BETWEEN 1 AND 120); trimmed on parse. */
const nameSchema = z.string().trim().min(1).max(120);
/** `max_occurrences` — CHECK (NULL OR BETWEEN 1 AND 1000). */
const maxOccurrencesSchema = z.number().int().min(1).max(1000);
/**
 * `interval_count` — the STATIC bound (1..52) here; the per-frequency cap
 * (weekly ≤ 52, else ≤ 12) needs the sibling `frequency` field and is applied
 * in the union-level `superRefine` below, on path `['interval_count']`.
 */
const intervalCountSchema = z.number().int().min(1).max(52);
/** `due_days` — CHECK (NULL OR BETWEEN 0 AND 365); AP/AR variants only. */
const dueDaysSchema = z.number().int().min(0).max(365);
const scheduleFields = {
    name: nameSchema,
    frequency: recurringFrequencySchema,
    interval_count: intervalCountSchema,
    /** The first occurrence AND the day-of-month anchor. `>= org-today` is asserted by the RPC (it owns "today"). */
    start_date: calendarDateSchema,
    end_date: calendarDateSchema.nullable().optional(),
    max_occurrences: maxOccurrencesSchema.nullable().optional(),
};
// ---------------------------------------------------------------------------
// The template body — one variant per document type
// ---------------------------------------------------------------------------
const apBillTemplateVariant = z
    .object({
    document_type: z.literal("ap_bill"),
    ...scheduleFields,
    due_days: dueDaysSchema,
    payload: recurringApBillPayloadSchema,
})
    .strict();
const arInvoiceTemplateVariant = z
    .object({
    document_type: z.literal("ar_invoice"),
    ...scheduleFields,
    due_days: dueDaysSchema,
    /**
     * `recurring_document_templates.auto_issue` (20261101120000): issue each
     * generated invoice on its occurrence date. Off by default, and absent on
     * an update keeps the stored value. When the occurrence date fails the
     * issue-date rule the invoice is left a draft and the occurrence names why
     * (`issue_deferred_code`). AR only — the other variants' `.strict()`
     * rejects the key.
     */
    auto_issue: z.boolean().optional(),
    payload: recurringArInvoicePayloadSchema,
})
    .strict();
const expenseTemplateVariant = z
    .object({
    document_type: z.literal("expense"),
    ...scheduleFields,
    payload: recurringExpensePayloadSchema,
})
    .strict();
const journalTemplateVariant = z
    .object({
    document_type: z.literal("journal"),
    ...scheduleFields,
    payload: recurringJournalPayloadSchema,
})
    .strict();
/**
 * The cross-field rules the object schemas cannot carry on their own. Runs on
 * the union output, so the `document_type` narrowing is exact.
 */
function refineTemplateBody(body, ctx) {
    const cap = maxIntervalCountFor(body.frequency);
    if (body.interval_count > cap) {
        ctx.addIssue({
            code: z.ZodIssueCode.too_big,
            maximum: cap,
            type: "number",
            inclusive: true,
            path: ["interval_count"],
            message: `interval_count must be at most ${cap} for ${body.frequency}`,
        });
    }
    // Lexicographic order IS date order for yyyy-MM-dd.
    if (body.end_date != null && body.end_date < body.start_date) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["end_date"],
            message: "end_date must be on or after start_date",
        });
    }
    if (body.document_type === "ap_bill" && !apBillPayeeXor(body.payload)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["payload"],
            message: "Exactly one of business_id or person_id is required",
        });
    }
}
const recurringTemplateBodySchema = z.discriminatedUnion("document_type", [
    apBillTemplateVariant,
    arInvoiceTemplateVariant,
    expenseTemplateVariant,
    journalTemplateVariant,
]);
/**
 * POST /orgs/:orgId/templates — `create_recurring_document_template`.
 * `organization_id` comes from the path (strict body prevents smuggling);
 * `owner_id`, `created_by`, `next_occurrence_date := start_date` are stamped
 * server-side. The route pre-checks every uuid in `payload` against the org
 * before calling the RPC (docs/BACKEND_API.md; plan §D).
 */
export const createRecurringTemplateSchema = recurringTemplateBodySchema.superRefine(refineTemplateBody);
/**
 * PATCH /orgs/:orgId/templates/:id — `update_recurring_document_template`.
 * FULL REPLACE, the same body as create: `document_type` is carried but
 * IMMUTABLE (the RPC answers `type_immutable` → 400 when it differs), and a
 * `start_date` change is accepted only while `occurrences_generated = 0`
 * (also RPC-enforced). Schedule changes recompute `next_occurrence_date`
 * server-side from `GREATEST(org-today, last_occurrence_date + 1)`.
 */
export const updateRecurringTemplateSchema = createRecurringTemplateSchema;
//# sourceMappingURL=recurring.js.map